"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { ProjectProps } from "./projectDetails";
import { AiOutlineClose } from "react-icons/ai";
import { SiGithub } from "react-icons/si";
import { BsLink45Deg } from "react-icons/bs";

type Props = {
    project: ProjectProps;
    onClose: () => void;
};

export default function ProjectModal({ project, onClose }: Props) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <button
                type="button"
                className="absolute inset-0 bg-black/50"
                aria-label="Close project details"
                onClick={onClose}
            />
            <motion.div
                className="relative mx-4 w-[95%] max-w-5xl rounded-lg bg-[#0b0b0d] p-6 text-white shadow-xl"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <button
                    onClick={onClose}
                    aria-label="Close project details"
                    className="absolute right-4 top-4 rounded-md bg-transparent p-2 text-white hover:opacity-80"
                >
                    <AiOutlineClose size={24} />
                </button>

                <div className="grid gap-6 md:grid-cols-2">
                    <div className="flex items-center justify-center">
                        <Image
                            src={project.image}
                            alt={project.name}
                            width={600}
                            height={400}
                            className="max-h-[320px] w-full rounded-md object-contain"
                        />
                    </div>
                    <div>
                        <h3 className="text-2xl font-semibold">{project.name}</h3>
                        <p className="mt-4 text-sm text-[#c7c7cd]">{project.description}</p>

                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            {project.technologies.map((Icon, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm text-[#e6e6ea]">
                                    <Icon />
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex gap-3">
                            {project.available && (
                                <a
                                    href={project.github}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 rounded bg-white/10 px-4 py-2 text-sm hover:bg-white/20"
                                >
                                    <SiGithub />
                                    <span>Repository</span>
                                </a>
                            )}

                            {project.available && (
                                <a
                                    href={project.demo}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 rounded bg-white/10 px-4 py-2 text-sm hover:bg-white/20"
                                >
                                    <BsLink45Deg />
                                    <span>Live Demo</span>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
